import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, Zap, Palette, Shield, Users } from 'lucide-react';
import { RootState } from '../store';
import { loginStart, loginSuccess, loginFailure } from '../store/slices/authSlice';
import { getApiUrl } from '../utils/api';
import { legalConfig } from '../config/legal';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

interface LoginResponse {
  success: boolean;
  token?: string;
  refreshToken?: string;
  user?: {
    id: string;
    email: string;
    name?: string;
    role?: string;
    permissions?: {
      screenSaverLock?: boolean;
      [key: string]: unknown;
    } | null;
    firstName?: string;
    lastName?: string;
    phone?: string;
    emailVerified?: boolean;
    createdAt: Date;
    updatedAt: Date;
  };
  message?: string;
}

export function LoginPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
    remember: false
  });
  const [showPassword, setShowPassword] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const userStr = localStorage.getItem('user');
      const parsed = userStr ? JSON.parse(userStr) : null;
      const userType = parsed?.userType as 'ADMIN' | 'PRACTITIONER' | 'EMPLOYEE' | undefined;
      if (userType === 'PRACTITIONER') navigate('/practitioner/dashboard', { replace: true });
      else if (userType === 'EMPLOYEE')   navigate('/employee/dashboard', { replace: true });
      else                                navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleInputChange = (field: keyof LoginFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      return;
    }

    dispatch(loginStart());

    try {
      // 1) Try admin login
      const adminResp = await fetch(getApiUrl('/api/auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password }),
      });
      const adminData: LoginResponse = await adminResp.json().catch(() => ({ success: false }));

      if (adminResp.ok && adminData.success && adminData.token && adminData.user) {
        dispatch(loginSuccess({
          user: {
            id: adminData.user.id,
            email: adminData.user.email,
            name: adminData.user.name || adminData.user.email,
            role: adminData.user.role,
            permissions: adminData.user.permissions,
            userType: 'ADMIN',
            createdAt: adminData.user.createdAt,
            updatedAt: adminData.user.updatedAt,
          } as any,
          token: adminData.token,
          refreshToken: (adminData as any).refreshToken,
        }));
        return;
      }

      // 2) Try practitioner login
      const pracResp = await fetch(getApiUrl('/api/practitioner-auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password }),
      });
      const pracData: LoginResponse = await pracResp.json().catch(() => ({ success: false }));

      if (pracResp.ok && pracData.success && pracData.token && pracData.user) {
        const firstName = pracData.user.firstName || '';
        const lastName = pracData.user.lastName || '';
        const displayName = `${firstName} ${lastName}`.trim() || pracData.user.email;

        dispatch(loginSuccess({
          user: {
            id: pracData.user.id,
            email: pracData.user.email,
            name: displayName,
            userType: 'PRACTITIONER',
            firstName,
            lastName,
            phone: pracData.user.phone,
            emailVerified: pracData.user.emailVerified,
            createdAt: pracData.user.createdAt,
            updatedAt: pracData.user.updatedAt,
          } as any,
          token: pracData.token,
          refreshToken: pracData.refreshToken,
        }));
        return;
      }

      // 3) Try employee login
      const empResp = await fetch(getApiUrl('/api/employee-auth/login'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password }),
      });
      const empData: LoginResponse = await empResp.json().catch(() => ({ success: false }));

      if (empResp.ok && empData.success && empData.token && empData.user) {
        const firstName = empData.user.firstName || '';
        const lastName  = empData.user.lastName  || '';
        dispatch(loginSuccess({
          user: {
            id: empData.user.id,
            email: empData.user.email,
            name: `${firstName} ${lastName}`.trim() || empData.user.email,
            userType: 'EMPLOYEE',
            firstName,
            lastName,
            phone: empData.user.phone,
            createdAt: empData.user.createdAt,
            updatedAt: empData.user.updatedAt,
          } as any,
          token: empData.token,
          refreshToken: (empData as any).refreshToken,
        }));
        return;
      }

      dispatch(loginFailure(
        (adminData as any)?.message ||
        (pracData as any)?.message  ||
        (empData as any)?.message   ||
        t('login.error'),
      ));
    } catch (error) {
      console.error('Login error:', error);
      dispatch(loginFailure('Network error. Please try again.'));
    }
  };

  const features = [
    {
      icon: Users,
      title: 'Gestion Clients',
      description: 'Gérez facilement vos clients et leurs informations'
    },
    {
      icon: Palette,
      title: 'Suivi Projets',
      description: 'Suivez tous vos projets tattoo et piercing'
    },
    {
      icon: Shield,
      title: 'Sécurité',
      description: 'Protocoles de sécurité et conformité RGPD'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-background to-accent-50 flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-primary-300 via-accent-500 to-secondary-400 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <div className="mb-8">
            <div className="flex items-center space-x-4 mb-6">
              <div className="flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-display font-bold">Digital Tattoo</h1>
                <p className="text-white/90">Studio Management System</p>
              </div>
            </div>
            
            <h2 className="text-4xl font-display font-bold mb-4 leading-tight">
              {t('login.branding.title')}
            </h2>
            <p className="text-xl text-white/90 leading-relaxed">
              {t('login.branding.subtitle')}
            </p>
          </div>

          <div className="space-y-6">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start space-x-4">
                <div className="flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{feature.title}</h3>
                  <p className="text-white/80">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-1/4 right-10 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-1/4 left-10 w-24 h-24 bg-white/10 rounded-full blur-xl"></div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Mobile Header */}
          <div className="text-center lg:hidden">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary-300 to-accent-500 rounded-xl">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-display font-bold text-textPrimary">
                Digital Tattoo
              </h1>
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-display font-bold text-textPrimary text-center">
              {t('login.title')}
            </h2>
            <p className="mt-2 text-center text-textSecondary">
              {t('login.subtitle')}
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            <fieldset disabled={loading} className={`space-y-4 ${loading ? 'opacity-60' : ''}`}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-textPrimary mb-2">
                  {t('login.email')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="input-field pl-10"
                    placeholder="admin@example.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-textPrimary mb-2">
                  {t('login.password')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="input-field pl-10 pr-10"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>
            </fieldset>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember"
                  name="remember"
                  type="checkbox"
                  checked={formData.remember}
                  onChange={(e) => handleInputChange('remember', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="remember" className="ml-2 block text-sm text-textSecondary">
                  {t('login.remember')}
                </label>
              </div>

              <div className="text-sm">
                <Link
                to="/forgot-password"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                {t('login.forgot')}
              </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('login.loading')}
                  </div>
                ) : (
                  t('login.button')
                )}
              </button>
            </div>
          </form>

          <p className="mt-6 text-xs text-textSecondary text-center leading-relaxed">
            Accès réservé au personnel habilité. Les données clients sont traitées conformément au RGPD.{' '}
            <a
              href={legalConfig.staffPrivacyNoticeUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-600 hover:text-primary-500 underline"
            >
              Charte confidentialité personnel
            </a>
            {' · '}
            <a href={`mailto:${legalConfig.privacyContactEmail}`} className="text-primary-600 hover:text-primary-500 underline">
              {legalConfig.privacyContactEmail}
            </a>
          </p>

          {/* Demo Credentials */}
          {/* <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800 font-medium mb-2">Identifiants de démonstration:</p>
            <p className="text-sm text-blue-700">Email: admin@example.com</p>
            <p className="text-sm text-blue-700">Mot de passe: Admin123!</p>
          </div> */}
        </div>
      </div>
    </div>
  );
}